import {
  getCusip,
  accountTypes,
  getAppSourceType,
  secondaryCommitMessage,
  updateRIWithholding,
  applicationStatusTypes,
  isRetirementIncomeFundPresent
} from '../shared/utils';
import {
  pollForSubmissionResponse,
  commitConsent,
  getUserExistingAgreements,
  loadUser,
  initiateAuthService
} from '../shared/loaders';
import {
  analyticsDataLayer,
  dispatchPageLoad
} from '../../analytics/analytics-helper';
import { isEmpty, mergeTyped, withDefault } from '../../core/util';

import { getExistingAgreementsService } from '../shared/services/existing-agreements-service';
import { DataLoader } from '../../core/types/data-loader';
import {
  ApplicationState,
  PartialStateStore,
  StateStore,
  StateStoreOrVoid
} from '../../core/types/application-state';

import { createTransferApplicationService } from '../shared/services/mutualfund-transfer-ira';
import { disqualifiedByCusip } from '../../components/ConfirmationSection/util';
import {
  accountTypesAnalytics,
  productTypesAnalytics
} from '../../analytics/util';
import { getCustodianDetailsByIDService } from '../shared/services/custodianByID';
import { encode_deviceprint as encodeDeviceprint } from '../../rsa-fingerprint';

// TODO we should have some kinda of files for these enums
// probably the same place as the ones in WelcomeScreen/utils
export enum IRAAccountTypes {
  TRADITIONAL = 'TRADITIONAL',
  SEP = 'SEP',
  ROTH = 'ROTH',
  ROLLOVER = 'ROLLOVER'
}

export type ToFromIRATypes = {
  toIRAAccountType: IRAAccountTypes;
  fromIRAAccountType: IRAAccountTypes;
};

export const commitCustodian: DataLoader = async ({
  chosen
}): Promise<StateStore> => {
  const { custodianID, custodianName } = chosen;

  const custodianDetails = await getCustodianDetailsByIDService(custodianID);

  // fixes regex failures on backend for addition spaces on end of values received
  const trimmedCustodianAddress = {
    street1: custodianDetails?.regularAddressLine1?.trim(),
    street2: custodianDetails?.regularAddressLine2?.trim(),
    city: custodianDetails?.regularAddressCity?.trim(),
    state: custodianDetails?.regularAddressState?.trim(),
    zip: custodianDetails?.regularAddressZip?.trim()
  };

  return {
    app: {
      custodian: {
        custodianID,
        custodianName,
        ...(Boolean(custodianDetails?.regularAddressLine1) && {
          custodianAddress: trimmedCustodianAddress
        })
      }
    },
    loadedData: {
      morningstarFamilyFunds: withDefault(custodianDetails?.funds, []),
      branchAddressRequired: custodianDetails?.branchAddressRequired
    },
    toraParticipant: custodianDetails?.toraParticipant,
    custodianCustomerServicePhone: custodianDetails?.customerServicePhone
  };
};

export const commitExternalAccount: DataLoader = ({
  inputs,
  chosen: {
    fundsInBrokerage,
    preliquidated,
    cusips,
    transferValidationMessage
  },
  store: { app, toraParticipant }
}): StateStore => {
  // DE70957 - if custom transfer amount validation messsage is present, bail out
  if (!isEmpty(transferValidationMessage)) {
    return { app, toraParticipant };
  }
  const { funds } = inputs;
  const simulateCusipsAsFunds = cusips?.map(cusip => {
    return { cusip };
  });
  const qualifiedToraParticipant =
    toraParticipant && !disqualifiedByCusip(simulateCusipsAsFunds);

  const res = {
    app: {
      existingAccounts: withDefault(app?.existingAccounts, []),
      externalAccounts: funds.map(
        (
          { accountName, accountNumber, transferAmount, transferBalance },
          idx
        ) => ({
          accountName,
          accountNumber,
          accountType: 'MUTUAL_FUND',
          estimatedTransferAmount: parseFloat(transferAmount),
          liquidationOption: transferBalance,
          cusip: withDefault(getCusip(idx, cusips), '')
        })
      )
    },
    fundsInBrokerage: Boolean(fundsInBrokerage),
    preliquidated: Boolean(preliquidated),
    toraParticipant: qualifiedToraParticipant
  };
  return res;
};

export const commitCustodianBranchAddress: DataLoader = ({
  inputs: {
    custodianDetailsStreet1,
    custodianDetailsStreet2,
    custodianDetailsCity,
    custodianDetailsState,
    custodianDetailsZip
  }
}): StateStore => {
  const custodianAddress = {
    street1: custodianDetailsStreet1,
    street2: custodianDetailsStreet2,
    city: custodianDetailsCity,
    state: custodianDetailsState,
    zip: custodianDetailsZip
  };
  return {
    app: {
      custodian: {
        custodianAddress
      }
    }
  };
};

// loads agreements for tranfer flows to account for RJ
export const loadTransferExistingAgreements = ({
  toIRAAccountType
}): DataLoader =>
  async function loadTransferAgreements(): Promise<PartialStateStore> {
    const existingAgreementsResults = await getExistingAgreementsService([
      accountTypes[`${toIRAAccountType}`]
    ]);

    return {
      ...(existingAgreementsResults && {
        existingAgreements: existingAgreementsResults
      })
    };
  };

export const loadLoginData: DataLoader = async (
  _,
  flowProps
): Promise<StateStoreOrVoid> => {
  const authDetails = await initiateAuthService();

  const { fromIRAAccountType = '', toIRAAccountType = '' } =
    flowProps?.iraAccountType || {};
  const queryParams = window.location.search;
  return {
    authenticated: authDetails?.authenticated,
    accountType: accountTypes.TRANSFER,
    ...(!isEmpty(queryParams) && {
      routeData: {
        queryString: queryParams
      }
    }),
    app: {
      toIRAAccountType,
      fromIRAAccountType
    }
  };
};

export const makeLoadProfileData = ({
  fromIRAAccountType,
  toIRAAccountType
}): DataLoader =>
  async function loadProfileData(
    a: ApplicationState
  ): Promise<StateStoreOrVoid> {
    const userResults = await loadUser(a);

    const existingAgreementsResults = await getUserExistingAgreements(
      userResults,
      [accountTypes[`${toIRAAccountType}`]]
    );

    // use IRA types commited to app if applicable
    const {
      store: {
        app: {
          toIRAAccountType: appToIraAccountType,
          fromIRAAccountType: appFromIraAccountType
        }
      }
    } = a;
    const toIraType = withDefault(appToIraAccountType, toIRAAccountType);
    const fromIraType = withDefault(appFromIraAccountType, fromIRAAccountType);
    // return object needs to be in this order
    // so existingAgreementsResults.app does not overwrite userResults.app
    return {
      ...(existingAgreementsResults && {
        existingAgreements: existingAgreementsResults
      }),
      ...mergeTyped<StateStoreOrVoid>(userResults, {
        app: { toIRAAccountType: toIraType, fromIRAAccountType: fromIraType },
        accountType: accountTypes.TRANSFER
      })
    };
  };

/* eslint-disable complexity */
export const createApplication: DataLoader = async ({
  store,
  inputs,
  chosen
}): Promise<StateStoreOrVoid> => {
  if (
    [
      'byop',
      'multiple',
      'existing_only',
      'rdf',
      'funding',
      'info',
      'contact'
    ].includes(chosen)
  ) {
    return { inReviewMode: true };
  }

  const { taxWithholdingOption, taxWithholdingPercent } = inputs;
  const { app, consumerName, chosenInvestments } = store;
  const { customer, termsIDs, newInvestments } = app;
  // use terms ids to create consent
  await commitConsent(termsIDs, customer);
  // terms ids should be an array of strings for service
  const stringTermsIDs = termsIDs?.map(id => id?.toString());

  // check for Retirement Income and tax withholding
  const hasRetrimentIncomeInvestment = isRetirementIncomeFundPresent(
    chosenInvestments
  );

  const finalizedNewInvestments = hasRetrimentIncomeInvestment
    ? updateRIWithholding(
        newInvestments,
        taxWithholdingOption,
        taxWithholdingPercent,
        chosenInvestments
      )
    : newInvestments;

  const updatedAppState = {
    ...store?.app,
    newInvestments: finalizedNewInvestments,
    termsIDs: stringTermsIDs,
    deviceToken: encodeDeviceprint()
  };

  // get the service error code and tracking id on the initial submission request
  const {
    serviceErrorCode,
    trackingIdentifier
  } = await createTransferApplicationService(updatedAppState, consumerName);

  const isSubmissionTimedOut = serviceErrorCode === 504;

  if (isSubmissionTimedOut) {
    return {
      loadedData: {
        is504Timeout: true
      },
      app: {
        applicationStatus: applicationStatusTypes.COMPLETED
      }
    };
  }

  const submissionResult = await pollForSubmissionResponse(trackingIdentifier);
  return submissionResult;
};
/* eslint-enable complexity */

// specifies loading message when creating account at end
createApplication.message = 'Creating your new account';
createApplication.secondaryMessage = secondaryCommitMessage;

export const updateAnalytics: DataLoader = async (
  { store },
  flowProps
): Promise<StateStoreOrVoid> => {
  const {
    app: { fromIRAAccountType }
  } = store;

  analyticsDataLayer({
    accountType: accountTypesAnalytics.TRANSFER[fromIRAAccountType],
    productType: productTypesAnalytics.MUTUAL_FUND,
    sourceType: getAppSourceType(store?.consumerName),
    userType:
      store?.authenticated && !store?.loadedData?.newUserCreated
        ? 'Existing'
        : 'New',
    amount: store?.amount,
    isComplete: Boolean(flowProps?.analytics?.isComplete),
    clientTier: store?.summitData?.assetTierID?.toString()
  });
  dispatchPageLoad(flowProps?.analytics.pageName);
};
